/**
 * PostProcessingManager - Manages all post-processing effects
 *
 * Uses BabylonJS DefaultRenderingPipeline for most effects (bloom, DOF, etc.)
 * and SSAORenderingPipeline for ambient occlusion.
 *
 * Effects are configured per-scene via PostProcessingConfig.
 */

import { Scene } from '@babylonjs/core/scene';
import { Camera } from '@babylonjs/core/Cameras/camera';
import { DefaultRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline';
import { SSAORenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/ssaoRenderingPipeline';
import { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { DepthOfFieldEffectBlurLevel } from '@babylonjs/core/PostProcesses/depthOfFieldEffect';
import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration';
import { Logger } from '../utils/Logger';
import type { PostProcessingConfig } from '../types/config';

// Side-effect imports for tree-shaking
import '@babylonjs/core/PostProcesses/RenderPipeline/postProcessRenderPipelineManagerSceneComponent';
import '@babylonjs/core/Rendering/depthRendererSceneComponent';

const logger = Logger.getLogger('PostProcessingManager');

/**
 * Converts hex color string to Color3
 */
function hexToColor3(hex: string): Color3 {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return new Color3(
      parseInt(result[1], 16) / 255,
      parseInt(result[2], 16) / 255,
      parseInt(result[3], 16) / 255
    );
  }
  return new Color3(0, 0, 0);
}

/**
 * Manages post-processing effects for a BabylonJS scene
 */
export class PostProcessingManager {
  private scene: Scene;
  private camera: Camera;
  private pipeline: DefaultRenderingPipeline | null = null;
  private ssaoPipeline: SSAORenderingPipeline | null = null;
  private glowLayer: GlowLayer | null = null;
  private currentConfig: PostProcessingConfig | null = null;

  constructor(scene: Scene, camera: Camera) {
    this.scene = scene;
    this.camera = camera;
    logger.debug('PostProcessingManager initialized');
  }

  /**
   * Apply a complete post-processing configuration
   */
  applyConfig(config: PostProcessingConfig | undefined): void {
    if (!config) {
      this.dispose();
      this.currentConfig = null;
      return;
    }

    this.currentConfig = config;
    logger.debug('Applying post-processing config:', config);

    // Create or get the default rendering pipeline
    this.ensurePipeline();

    // Apply each effect
    this.applyBloom(config.bloom);
    this.applyDepthOfField(config.depthOfField);
    this.applyChromaticAberration(config.chromaticAberration);
    this.applyGrain(config.grain);
    this.applySharpen(config.sharpen);
    this.applyVignette(config.vignette);
    this.applyAntialiasing(config.fxaa, config.msaaSamples);
    this.applyImageProcessing(config.imageProcessing);
    this.applyGlowLayer(config.glowLayer);
    this.applySSAO(config.ssao);
  }

  /**
   * Ensure the default rendering pipeline exists
   */
  private ensurePipeline(): void {
    if (!this.pipeline) {
      this.pipeline = new DefaultRenderingPipeline(
        'defaultPipeline',
        true, // HDR enabled for better bloom quality
        this.scene,
        [this.camera]
      );
      logger.debug('Created DefaultRenderingPipeline');
    }
  }

  /**
   * Apply bloom effect
   */
  private applyBloom(config: PostProcessingConfig['bloom']): void {
    if (!this.pipeline) return;

    if (config?.enabled) {
      this.pipeline.bloomEnabled = true;
      this.pipeline.bloomThreshold = config.threshold ?? 0.9;
      this.pipeline.bloomWeight = config.weight ?? 0.15;
      this.pipeline.bloomKernel = config.kernel ?? 64;
      this.pipeline.bloomScale = config.scale ?? 0.5;
      logger.debug('Bloom enabled:', { threshold: this.pipeline.bloomThreshold, weight: this.pipeline.bloomWeight });
    } else {
      this.pipeline.bloomEnabled = false;
    }
  }

  /**
   * Apply depth of field effect
   */
  private applyDepthOfField(config: PostProcessingConfig['depthOfField']): void {
    if (!this.pipeline) return;

    if (config?.enabled) {
      this.pipeline.depthOfFieldEnabled = true;

      // Set blur level
      const blurLevelMap: Record<string, DepthOfFieldEffectBlurLevel> = {
        low: DepthOfFieldEffectBlurLevel.Low,
        medium: DepthOfFieldEffectBlurLevel.Medium,
        high: DepthOfFieldEffectBlurLevel.High,
      };
      this.pipeline.depthOfFieldBlurLevel = blurLevelMap[config.blurLevel ?? 'medium'] ?? DepthOfFieldEffectBlurLevel.Medium;

      // Set DOF parameters
      this.pipeline.depthOfField.focusDistance = config.focusDistance ?? 2000;
      this.pipeline.depthOfField.focalLength = config.focalLength ?? 50;
      this.pipeline.depthOfField.fStop = config.fStop ?? 1.4;
      logger.debug('DOF enabled:', { focusDistance: config.focusDistance, fStop: config.fStop });
    } else {
      this.pipeline.depthOfFieldEnabled = false;
    }
  }

  /**
   * Apply chromatic aberration effect
   */
  private applyChromaticAberration(config: PostProcessingConfig['chromaticAberration']): void {
    if (!this.pipeline) return;

    if (config?.enabled) {
      this.pipeline.chromaticAberrationEnabled = true;
      this.pipeline.chromaticAberration.aberrationAmount = config.amount ?? 30;
      this.pipeline.chromaticAberration.radialIntensity = config.radialIntensity ?? 3;
      logger.debug('Chromatic aberration enabled');
    } else {
      this.pipeline.chromaticAberrationEnabled = false;
    }
  }

  /**
   * Apply film grain effect
   */
  private applyGrain(config: PostProcessingConfig['grain']): void {
    if (!this.pipeline) return;

    if (config?.enabled) {
      this.pipeline.grainEnabled = true;
      this.pipeline.grain.intensity = config.intensity ?? 30;
      this.pipeline.grain.animated = config.animated ?? false;
      logger.debug('Grain enabled');
    } else {
      this.pipeline.grainEnabled = false;
    }
  }

  /**
   * Apply sharpening effect
   */
  private applySharpen(config: PostProcessingConfig['sharpen']): void {
    if (!this.pipeline) return;

    if (config?.enabled) {
      this.pipeline.sharpenEnabled = true;
      this.pipeline.sharpen.edgeAmount = config.edgeAmount ?? 0.3;
      this.pipeline.sharpen.colorAmount = config.colorAmount ?? 1.0;
      logger.debug('Sharpen enabled');
    } else {
      this.pipeline.sharpenEnabled = false;
    }
  }

  /**
   * Apply vignette effect
   */
  private applyVignette(config: PostProcessingConfig['vignette']): void {
    if (!this.pipeline) return;

    if (config?.enabled) {
      this.pipeline.imageProcessing.vignetteEnabled = true;
      this.pipeline.imageProcessing.vignetteWeight = config.weight ?? 1.5;
      if (config.color) {
        const color = hexToColor3(config.color);
        this.pipeline.imageProcessing.vignetteColor = new Color4(color.r, color.g, color.b, 1);
      }
      logger.debug('Vignette enabled');
    } else if (this.pipeline.imageProcessing) {
      this.pipeline.imageProcessing.vignetteEnabled = false;
    }
  }

  /**
   * Apply antialiasing (FXAA and/or MSAA)
   */
  private applyAntialiasing(fxaa?: boolean, msaaSamples?: number): void {
    if (!this.pipeline) return;

    this.pipeline.fxaaEnabled = fxaa ?? false;

    // MSAA samples (1 = disabled, 2/4/8 = enabled)
    if (msaaSamples && msaaSamples > 1) {
      this.pipeline.samples = msaaSamples;
    } else {
      this.pipeline.samples = 1;
    }

    if (fxaa || (msaaSamples && msaaSamples > 1)) {
      logger.debug('Antialiasing enabled:', { fxaa, msaa: msaaSamples });
    }
  }

  /**
   * Apply image processing settings (exposure, contrast, tone mapping)
   */
  private applyImageProcessing(config: PostProcessingConfig['imageProcessing']): void {
    if (!this.pipeline) return;

    // Image processing is always enabled when pipeline exists
    this.pipeline.imageProcessingEnabled = true;

    if (config) {
      const ip = this.pipeline.imageProcessing;
      ip.exposure = config.exposure ?? 1.0;
      ip.contrast = config.contrast ?? 1.0;
      // Note: saturation is handled through colorCurves in BabylonJS

      if (config.toneMappingEnabled !== undefined) {
        ip.toneMappingEnabled = config.toneMappingEnabled;
      }

      if (config.toneMappingType) {
        const typeMap: Record<string, number> = {
          standard: ImageProcessingConfiguration.TONEMAPPING_STANDARD,
          aces: ImageProcessingConfiguration.TONEMAPPING_ACES,
          khr_pbr_neutral: ImageProcessingConfiguration.TONEMAPPING_KHR_PBR_NEUTRAL,
        };
        ip.toneMappingType = typeMap[config.toneMappingType] ?? ImageProcessingConfiguration.TONEMAPPING_STANDARD;
      }

      logger.debug('Image processing updated:', { exposure: config.exposure, contrast: config.contrast });
    }
  }

  /**
   * Apply glow layer for emissive materials
   */
  private applyGlowLayer(config: PostProcessingConfig['glowLayer']): void {
    if (config?.enabled) {
      if (!this.glowLayer) {
        this.glowLayer = new GlowLayer('glowLayer', this.scene);
      }
      this.glowLayer.intensity = config.intensity ?? 1.0;
      this.glowLayer.blurKernelSize = config.blurKernelSize ?? 16;
      logger.debug('Glow layer enabled');
    } else if (this.glowLayer) {
      this.glowLayer.dispose();
      this.glowLayer = null;
    }
  }

  /**
   * Apply SSAO (Screen Space Ambient Occlusion)
   */
  private applySSAO(config: PostProcessingConfig['ssao']): void {
    if (config?.enabled) {
      if (!this.ssaoPipeline) {
        const ratio = config.ratio ?? 0.75;
        this.ssaoPipeline = new SSAORenderingPipeline(
          'ssaoPipeline',
          this.scene,
          ratio,
          [this.camera]
        );
      }

      // SSAO doesn't have direct radius/intensity setters in the main pipeline
      // The effect is controlled by the ratio and internal parameters
      // For more control, would need SSAORenderingPipeline2 which has more options
      logger.debug('SSAO enabled with ratio:', config.ratio ?? 0.75);
    } else if (this.ssaoPipeline) {
      this.ssaoPipeline.dispose();
      this.ssaoPipeline = null;
    }
  }

  /**
   * Update a single effect without recreating the entire pipeline
   */
  updateEffect(effectName: keyof PostProcessingConfig, config: PostProcessingConfig[keyof PostProcessingConfig]): void {
    if (!this.currentConfig) {
      this.currentConfig = {};
    }

    // Update the config
    (this.currentConfig as Record<string, unknown>)[effectName] = config;

    // Apply just that effect
    this.ensurePipeline();

    switch (effectName) {
      case 'bloom':
        this.applyBloom(config as PostProcessingConfig['bloom']);
        break;
      case 'depthOfField':
        this.applyDepthOfField(config as PostProcessingConfig['depthOfField']);
        break;
      case 'chromaticAberration':
        this.applyChromaticAberration(config as PostProcessingConfig['chromaticAberration']);
        break;
      case 'grain':
        this.applyGrain(config as PostProcessingConfig['grain']);
        break;
      case 'sharpen':
        this.applySharpen(config as PostProcessingConfig['sharpen']);
        break;
      case 'vignette':
        this.applyVignette(config as PostProcessingConfig['vignette']);
        break;
      case 'fxaa':
        this.applyAntialiasing(config as boolean, this.currentConfig.msaaSamples);
        break;
      case 'msaaSamples':
        this.applyAntialiasing(this.currentConfig.fxaa, config as number);
        break;
      case 'imageProcessing':
        this.applyImageProcessing(config as PostProcessingConfig['imageProcessing']);
        break;
      case 'glowLayer':
        this.applyGlowLayer(config as PostProcessingConfig['glowLayer']);
        break;
      case 'ssao':
        this.applySSAO(config as PostProcessingConfig['ssao']);
        break;
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): PostProcessingConfig | null {
    return this.currentConfig;
  }

  /**
   * Check if any effects are currently active
   */
  hasActiveEffects(): boolean {
    return this.pipeline !== null || this.ssaoPipeline !== null || this.glowLayer !== null;
  }

  /**
   * Update the camera reference (e.g., when camera changes)
   */
  updateCamera(camera: Camera): void {
    this.camera = camera;

    // Re-attach pipelines to new camera
    if (this.pipeline) {
      this.scene.postProcessRenderPipelineManager.attachCamerasToRenderPipeline(
        'defaultPipeline',
        camera
      );
    }

    if (this.ssaoPipeline) {
      this.scene.postProcessRenderPipelineManager.attachCamerasToRenderPipeline(
        'ssaoPipeline',
        camera
      );
    }
  }

  /**
   * Dispose all effects and clean up resources
   */
  dispose(): void {
    if (this.pipeline) {
      this.pipeline.dispose();
      this.pipeline = null;
    }

    if (this.ssaoPipeline) {
      this.ssaoPipeline.dispose();
      this.ssaoPipeline = null;
    }

    if (this.glowLayer) {
      this.glowLayer.dispose();
      this.glowLayer = null;
    }

    this.currentConfig = null;
    logger.debug('PostProcessingManager disposed');
  }
}
